import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { FileText, Mail, MessageCircle, Download, RefreshCw, Edit, Trash2, AlertCircle, Plus, Check } from 'lucide-react';

const buildMonths = () => {
  const out = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({
      value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleString('en-US', { month: 'long', year: 'numeric' }),
    });
  }
  return out;
};

export default function TeacherReportCardManager() {
  const months = useMemo(buildMonths, []);
  const [students, setStudents] = useState([]);
  const [cards, setCards] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(months[0].value);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, cRes] = await Promise.all([
        api.get('/teachers/students'),
        api.get('/teacher/report-cards'),
      ]);
      setStudents(sRes.data || []);
      setCards(cRes.data || []);
    } catch { toast.error('Failed to load report card data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const cardFor = (studentId, month) =>
    cards.find(c => c.student_id === studentId && c.month === month);

  const generate = async (studentId) => {
    setLoading(true);
    try {
      const res = await api.post('/teacher/report-card/auto-generate', { student_id: studentId, month: selectedMonth });
      toast.success('Report card generated. Review and finalize.');
      await fetchData();
      setEditing(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Auto-generate failed');
    } finally { setLoading(false); }
  };

  const downloadPdf = async (cardId, studentName, month) => {
    try {
      const res = await api.get(`/teacher/report-card/${cardId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `ReportCard_${studentName.replace(/\s+/g, '_')}_${month}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('PDF download failed'); }
  };

  const sendEmail = async (cardId) => {
    try {
      const res = await api.post(`/teacher/report-card/${cardId}/email`);
      toast.success(`Sent to ${res.data.emailed_to}`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Email failed'); }
  };

  const sendWhatsApp = async (cardId) => {
    try {
      const res = await api.get(`/teacher/report-card/${cardId}/whatsapp-link`);
      window.open(res.data.url, '_blank', 'noopener');
    } catch { toast.error('Failed to build WhatsApp link'); }
  };

  const deleteCard = async (cardId) => {
    if (!window.confirm('Delete this report card?')) return;
    try {
      await api.delete(`/teacher/report-card/${cardId}`);
      toast.success('Deleted');
      fetchData();
    } catch { toast.error('Delete failed'); }
  };

  // Pending list = previous-month students without a card
  const prevMonth = months[1]?.value;
  const pending = students.filter(s => prevMonth && !cardFor(s.id, prevMonth));

  return (
    <div className="p-3 sm:p-6 space-y-5 max-w-6xl">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Report Cards</h1>
          <p className="text-xs text-slate-500 mt-0.5">Generate, review, and share monthly progress reports.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-44" data-testid="rc-month-select"><SelectValue /></SelectTrigger>
            <SelectContent>{months.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Pending reminders */}
      {pending.length > 0 && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="rc-pending-banner">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-amber-700 mt-0.5" />
            <div className="flex-1">
              <div className="font-bold text-amber-900 text-sm">Pending: {months[1].label}</div>
              <div className="text-xs text-amber-800 mt-0.5">{pending.length} student{pending.length !== 1 ? 's' : ''} still need a report for last month.</div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {pending.slice(0, 8).map(s => (
                  <button key={s.id}
                    onClick={() => { setSelectedMonth(prevMonth); generate(s.id); }}
                    className="px-2.5 py-1 rounded-full text-xs bg-white border border-amber-300 text-amber-900 hover:bg-amber-100">
                    {s.name} +
                  </button>
                ))}
                {pending.length > 8 && <span className="text-xs text-amber-700 self-center">+{pending.length - 8} more</span>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Per-student rows */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-slate-50 text-[11px] font-bold text-slate-600 uppercase">
          <div className="col-span-4">Student</div>
          <div className="col-span-2 text-center">Status</div>
          <div className="col-span-2 text-center">Month Classes</div>
          <div className="col-span-4 text-center">Actions</div>
        </div>
        {students.length === 0 && (
          <div className="px-4 py-6 text-sm text-slate-400 text-center">No mapped students yet.</div>
        )}
        {students.map(s => {
          const c = cardFor(s.id, selectedMonth);
          return (
            <div key={s.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-t border-slate-100 items-center" data-testid={`rc-row-${s.id}`}>
              <div className="col-span-4">
                <div className="font-semibold text-sm text-slate-900">{s.name}</div>
                <div className="text-[11px] text-slate-500">{s.citt_id || '-'}{s.grade ? ` · Gr ${s.grade}` : ''}</div>
              </div>
              <div className="col-span-2 text-center">
                {c ? (
                  c.finalized ? (
                    <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold">FINALIZED</span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-bold">DRAFT</span>
                  )
                ) : (
                  <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[10px] font-bold">NOT GENERATED</span>
                )}
              </div>
              <div className="col-span-2 text-center text-sm font-semibold text-slate-700">
                {c ? (c.month_classes_count ?? c.classes_detail?.length ?? 0) : '—'}
              </div>
              <div className="col-span-4 flex justify-center gap-1">
                {!c && (
                  <Button size="sm" onClick={() => generate(s.id)} disabled={loading} className="bg-blue-600 hover:bg-blue-700 text-white h-7 text-xs" data-testid={`rc-generate-${s.id}`}>
                    <Plus className="w-3 h-3 mr-1" /> Generate
                  </Button>
                )}
                {c && (
                  <>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-blue-600" onClick={() => setEditing(c)} title="Edit / Finalize" data-testid={`rc-edit-${c.id}`}>
                      <Edit className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-emerald-600" onClick={() => sendEmail(c.id)} title="Email to Parent" data-testid={`rc-email-${c.id}`}>
                      <Mail className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-green-600" onClick={() => sendWhatsApp(c.id)} title="WhatsApp Share" data-testid={`rc-whatsapp-${c.id}`}>
                      <MessageCircle className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-purple-600" onClick={() => downloadPdf(c.id, s.name, c.month)} title="Download PDF" data-testid={`rc-pdf-${c.id}`}>
                      <Download className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-red-500" onClick={() => deleteCard(c.id)} title="Delete">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Edit Dialog */}
      <ReportCardEditor card={editing} onClose={() => setEditing(null)} onSaved={fetchData} />
    </div>
  );
}

function ReportCardEditor({ card, onClose, onSaved }) {
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (card) {
      setForm({
        overall_progress: card.overall_progress || '',
        teacher_feedback: card.teacher_feedback || '',
        areas_of_improvement: card.areas_of_improvement || '',
        final_grade: card.final_grade || '',
        concepts_covered: (card.concepts_covered || []).join(', '),
        classes_detail: card.classes_detail || [],
        finalized: card.finalized || false,
      });
    } else { setForm(null); }
  }, [card]);

  if (!card || !form) return null;

  const save = async (finalize = false) => {
    try {
      await api.put(`/teacher/report-card/${card.id}`, {
        overall_progress: form.overall_progress,
        teacher_feedback: form.teacher_feedback,
        areas_of_improvement: form.areas_of_improvement,
        final_grade: form.final_grade,
        concepts_covered: form.concepts_covered.split(',').map(s => s.trim()).filter(Boolean),
        classes_detail: form.classes_detail,
        finalized: finalize || form.finalized,
      });
      toast.success(finalize ? 'Finalized' : 'Saved');
      onSaved && onSaved();
      onClose();
    } catch { toast.error('Save failed'); }
  };

  return (
    <Dialog open={!!card} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Report Card — {card.student_name} · {card.month_label}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Final Grade</Label>
              <Input value={form.final_grade} onChange={e => setForm({ ...form, final_grade: e.target.value })} placeholder="A+, A, B+, etc." />
            </div>
            <div>
              <Label>Concepts Covered (comma-separated)</Label>
              <Input value={form.concepts_covered} onChange={e => setForm({ ...form, concepts_covered: e.target.value })} placeholder="Loops, Variables, Events" />
            </div>
          </div>
          <div>
            <Label>Overall Progress</Label>
            <Textarea rows={3} value={form.overall_progress} onChange={e => setForm({ ...form, overall_progress: e.target.value })} placeholder="Summarize what the student achieved this month…" />
          </div>
          <div>
            <Label>Teacher Feedback</Label>
            <Textarea rows={3} value={form.teacher_feedback} onChange={e => setForm({ ...form, teacher_feedback: e.target.value })} placeholder="Personal note to the parent / student…" />
          </div>
          <div>
            <Label>Areas of Improvement</Label>
            <Textarea rows={2} value={form.areas_of_improvement} onChange={e => setForm({ ...form, areas_of_improvement: e.target.value })} />
          </div>

          <div>
            <Label>Class-wise Details ({form.classes_detail.length})</Label>
            <div className="space-y-2 max-h-60 overflow-y-auto border border-slate-200 rounded-lg p-2">
              {form.classes_detail.length === 0 && <p className="text-xs text-slate-400 text-center py-2">No completed classes for this month yet.</p>}
              {form.classes_detail.map((c, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-start text-xs">
                  <div className="col-span-2 text-slate-500">{c.date}</div>
                  <Input className="col-span-3 h-7 text-xs" value={c.class_name || ''} onChange={e => { const arr = [...form.classes_detail]; arr[i] = { ...c, class_name: e.target.value }; setForm({ ...form, classes_detail: arr }); }} />
                  <Input className="col-span-3 h-7 text-xs" value={c.concepts || ''} placeholder="Concepts" onChange={e => { const arr = [...form.classes_detail]; arr[i] = { ...c, concepts: e.target.value }; setForm({ ...form, classes_detail: arr }); }} />
                  <Input className="col-span-4 h-7 text-xs" value={c.feedback || ''} placeholder="Feedback" onChange={e => { const arr = [...form.classes_detail]; arr[i] = { ...c, feedback: e.target.value }; setForm({ ...form, classes_detail: arr }); }} />
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button variant="outline" onClick={() => save(false)} data-testid="rc-save-draft">Save Draft</Button>
            <Button onClick={() => save(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="rc-finalize">
              <Check className="w-4 h-4 mr-1" /> Finalize
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
